import { Suspense, lazy } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  useNavigationType,
} from "react-router-dom";
import AnimatedRoutes, { AnimatedRoute } from "react-router-animated";
import type { RouteItem } from "react-router-animated";

import RootLayout from "@web/app/root-layout";
import { AuthProvider } from "@web/components/auth-provider";
import { isLikelyDesktop } from "@web/lib/mobile-app";

// ── Lazy pages ────────────────────────────────────────────────────────────
// Public / auth flow
const Landing = lazy(() => import("@web/app/page"));
const Login = lazy(() => import("@web/app/login/page"));
const Signup = lazy(() => import("@web/app/signup/page"));
const ForgotPassword = lazy(() => import("@web/app/forgot-password/page"));
const PasswordResetSent = lazy(
  () => import("@web/app/password-reset-sent/page"),
);
const RecoveryId = lazy(() => import("@web/app/recovery/[id]/page"));
const RecoverySuccess = lazy(() => import("@web/app/recovery/success/page"));
const NotFound = lazy(() => import("@web/app/not-found/page"));
const DesktopRequired = lazy(() => import("@web/app/desktop-required/page"));
// Agent surfaces — the mobile components directly (this router only runs on the
// mobile app, so no need to go through the target-aware page wrappers).
const MobileChat = lazy(() =>
  import("@mobile/features/ai/MobileChat").then((m) => ({
    default: m.MobileChat,
  })),
);
const MobileChatThreadPage = lazy(() =>
  import("@mobile/features/ai/MobileChat").then((m) => ({
    default: m.MobileChatThreadPage,
  })),
);
const MobileAiProposals = lazy(() =>
  import("@mobile/features/ai-pre-orders/MobileAiProposals").then((m) => ({
    default: m.MobileAiProposals,
  })),
);
const MobileProposalReviewPage = lazy(() =>
  import("@mobile/features/ai-pre-orders/MobileProposalReviewPage").then(
    (m) => ({ default: m.MobileProposalReviewPage }),
  ),
);
// Compte tab → a lean mobile account view (details + disconnect), not the
// desktop preferences settings page.
const MobileAccount = lazy(
  () => import("@mobile/features/preferences/MobileAccount"),
);
// Sub-routes removed — preferences is now a flat single page.
const Profile = lazy(() => import("@web/app/profile/page"));
const ConfirmEmail = lazy(() => import("@web/app/confirm-email/[id]/page"));

function Public({ children }: { readonly children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

// Catch-all fallback. The lean tree omits full-app routes (/superadmin, /recap,
// …), so an unmatched path is usually a desktop-only page the viewport is too
// narrow for. A desktop browser (mouse + hover) that has shrunk below the
// breakpoint gets a "widen your window" hint; a genuine mobile device — which
// cannot widen anything — gets the normal 404.
function RouteFallback() {
  return isLikelyDesktop() ? <DesktopRequired /> : <NotFound />;
}

// Auth-gated surfaces: AuthProvider redirects to /login when there's no session.
function Protected({ children }: { readonly children: React.ReactNode }) {
  return (
    <AuthProvider>
      <Suspense fallback={null}>{children}</Suspense>
    </AuthProvider>
  );
}

// Per-route "level" keyed by the AnimatedRoute `id`. react-router-animated sorts
// routes by this (compare) and slides forward when the level increases, back
// when it decreases — so sibling tabs slide left/right and each list's detail
// route (one level above its list) slides in/out. Public/auth routes use
// animation="none", so their level is irrelevant (defaults to 0).
const ROUTE_LEVELS: Record<string, number> = {
  ai: 0,
  "ai-detail": 1,
  "ai-proposals": 2,
  "ai-proposals-detail": 3,
  preferences: 4,
  profile: 6,
};

function compareRoutes(a: RouteItem, b: RouteItem): number {
  const level = (id: string) => ROUTE_LEVELS[id] ?? 0;
  return level(a.id) - level(b.id);
}

/**
 * Lean mobile app router (pwa/capacitor targets). Tier 1 of the Mobile epic =
 * the three Agent surfaces (Chat /ai, Propositions /ai-proposals, Compte
 * /preferences) plus the auth flow.
 *
 * One <BrowserRouter> with react-router-animated's <AnimatedRoutes> as the
 * single top-level route set, so navigations slide (tabs left/right, list↔detail
 * forward/back). RootLayout wraps it as a plain shell (providers + bottom nav via
 * AppSidebar), so the nav stays put while pages slide. No data-router/splat
 * nesting — that remounted AnimatedRoutes on child-path nav and killed the slide.
 *
 * Animation is forward-only: PUSH navigations (opening a chat, switching tabs)
 * slide, but POP navigations (the iOS home-screen PWA's native swipe-back, the
 * in-app back arrow's navigate(-1), browser back) swap instantly. Otherwise the
 * library's slide stacks on top of the OS's own interactive swipe-back animation
 * and you get a doubled, snapping transition when leaving a chat — the platform
 * owns the back animation, we only own forward.
 */
function AnimatedAppRoutes() {
  // Slide only forward (PUSH) navigations. POP (native swipe-back, in-app back
  // arrow, browser back) and REPLACE (redirects, e.g. <Navigate replace>) swap
  // instantly — POP because the OS owns the back animation (see the doc comment
  // above), REPLACE because a redirect isn't a user-initiated forward step.
  const animation = useNavigationType() === "PUSH" ? "slide" : "none";
  return (
    <AnimatedRoutes
      duration={250}
      compare={compareRoutes}
      animation={animation}
    >
      {/* Public / auth flow — no slide */}
      <AnimatedRoute
        id="root"
        path="/"
        animation="none"
        element={
          <Public>
            <Landing />
          </Public>
        }
      />
      <AnimatedRoute
        id="login"
        path="/login"
        animation="none"
        element={
          <Public>
            <Login />
          </Public>
        }
      />
      <AnimatedRoute
        id="signup"
        path="/signup"
        animation="none"
        element={
          <Public>
            <Signup />
          </Public>
        }
      />
      <AnimatedRoute
        id="forgot-password"
        path="/forgot-password"
        animation="none"
        element={
          <Public>
            <ForgotPassword />
          </Public>
        }
      />
      <AnimatedRoute
        id="password-reset-sent"
        path="/password-reset-sent"
        animation="none"
        element={
          <Public>
            <PasswordResetSent />
          </Public>
        }
      />
      <AnimatedRoute
        id="recovery-id"
        path="/recovery/:id"
        animation="none"
        element={
          <Public>
            <RecoveryId />
          </Public>
        }
      />
      <AnimatedRoute
        id="recovery-success"
        path="/recovery/success"
        animation="none"
        element={
          <Public>
            <RecoverySuccess />
          </Public>
        }
      />

      {/* Agent surfaces — slide by level */}
      <Route
        id="ai"
        path="/ai"
        element={
          <Protected>
            <MobileChat />
          </Protected>
        }
      />
      <AnimatedRoute
        id="ai-detail"
        path="/ai/:sessionId"
        element={
          <Protected>
            <MobileChatThreadPage />
          </Protected>
        }
      />
      <Route
        id="ai-proposals"
        path="/ai-proposals"
        element={
          <Protected>
            <MobileAiProposals />
          </Protected>
        }
      />
      <AnimatedRoute
        id="ai-proposals-detail"
        path="/ai-proposals/:proposalId"
        element={
          <Protected>
            <MobileProposalReviewPage />
          </Protected>
        }
      />
      <Route
        id="preferences"
        path="/preferences"
        element={
          <Protected>
            <MobileAccount />
          </Protected>
        }
      />
      {/* Legacy sub-routes redirect so bookmarks/history don't 404. */}
      <Route
        path="/preferences/general"
        element={<Navigate to="/preferences" replace />}
      />
      <Route
        path="/preferences/sidebar"
        element={<Navigate to="/preferences" replace />}
      />

      <Route
        id="profile"
        path="/profile"
        element={
          <Protected>
            <Profile />
          </Protected>
        }
      />
      {/* Email-confirmation links must resolve on the mobile app too
              (otherwise they 404 for phone-viewport / capacitor users). */}
      <Route
        id="confirm-email"
        path="/confirm-email/:id"
        element={
          <Protected>
            <ConfirmEmail />
          </Protected>
        }
      />

      {/* 404 — or a "widen your window" hint for desktop-only routes when a
          desktop browser has shrunk into the lean tree (see RouteFallback). */}
      <AnimatedRoute
        id="notfound"
        path="*"
        animation="none"
        element={
          <Public>
            <RouteFallback />
          </Public>
        }
      />
    </AnimatedRoutes>
  );
}

export function MobileRouter() {
  return (
    <BrowserRouter>
      <RootLayout>
        <AnimatedAppRoutes />
      </RootLayout>
    </BrowserRouter>
  );
}
